// src/features/messages/components/AudienceSelector.jsx
import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Checkbox,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Groups as GroupsIcon, Badge as RoleIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ProfilePicture from '../../../components/common/ProfilePicture';

/** IDs of everyone the chosen audience reaches, without the sender */
export const recipientIds = (value, options, currentUserId) => {
  const ids = new Set();
  if (!options) return ids;
  if (value.all) {
    options.staff.forEach(s => ids.add(s.staff_member_id));
  } else {
    value.staffIds.forEach(id => ids.add(id));
    options.roles
      .filter(r => value.roleIds.includes(r.role_id))
      .forEach(r => r.staff_member_ids.forEach(id => ids.add(id)));
  }
  ids.delete(currentUserId);
  return ids;
};

// Groups and people share one card grid; wide enough that most names fit on one
// line, which on a phone means one column (two split long role names mid-word)
const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(auto-fill, minmax(220px, 1fr))' },
  gap: { xs: 2, sm: 2.5 },
};

const GROUP_AVATAR_SX = { width: 26, height: 26, bgcolor: 'primary.main', color: 'primary.contrastText', '& svg': { fontSize: 16 } };

/** A checkable card: the whole card is the checkbox label */
function OptionCard({ checked, onToggle, disabled, avatar, title, subtitle }) {
  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        minWidth: 0,
        px: 3,
        py: 2,
        border: 1,
        borderRadius: 1,
        borderColor: checked ? 'primary.main' : 'divider',
        bgcolor: checked ? (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.06) : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'border-color 120ms, background-color 120ms',
        ...(!disabled && { '&:hover': { borderColor: 'primary.main' } }),
      }}
    >
      <Checkbox size="small" checked={checked} onChange={onToggle} disabled={disabled} sx={{ p: 0 }} />
      {avatar}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={checked ? 600 : 400}
          sx={{
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * Pick who gets a message: groups (everyone, or one or more roles) or chosen
 * people, never both. Ticking a person clears the groups and ticking a group
 * clears the people, so what is ticked is always exactly who it goes to.
 * "Everyone" and a role exclude each other the same way.
 *
 * `value` is { all, roleIds, staffIds }; `options` comes from
 * GET /messages/audience-options. The sender never receives their own message,
 * so they are left out of the counts and the people grid.
 */
export default function AudienceSelector({ value, onChange, options, currentUserId, disabled = false }) {
  const { t } = useTranslation(['messages']);
  const [query, setQuery] = useState('');
  const staff = useMemo(
    () => (options?.staff ?? []).filter(s => s.staff_member_id !== currentUserId),
    [options, currentUserId],
  );
  const roles = useMemo(() => options?.roles ?? [], [options]);
  const nameById = useMemo(() => Object.fromEntries(staff.map(s => [s.staff_member_id, s.name])), [staff]);

  const recipients = useMemo(
    () => recipientIds(value, options, currentUserId),
    [value, options, currentUserId],
  );

  const visibleStaff = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return needle ? staff.filter(s => s.name.toLocaleLowerCase().includes(needle)) : staff;
  }, [staff, query]);

  const toggleAll = () => onChange(value.all
    ? { ...value, all: false }
    : { all: true, roleIds: [], staffIds: [] });

  const toggleRole = (roleId) => onChange(value.roleIds.includes(roleId)
    ? { ...value, roleIds: value.roleIds.filter(id => id !== roleId) }
    : { all: false, roleIds: [...value.roleIds, roleId], staffIds: [] });

  const toggleStaff = (staffId) => onChange(value.staffIds.includes(staffId)
    ? { ...value, staffIds: value.staffIds.filter(id => id !== staffId) }
    : { all: false, roleIds: [], staffIds: [...value.staffIds, staffId] });

  let summary = t('messages:compose.no_recipients');
  if (value.all) summary = t('messages:compose.summary_all', { count: recipients.size });
  else if (recipients.size) summary = t('messages:compose.summary', { count: recipients.size });

  return (
    <Stack spacing={6}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2.5 }}>{t('messages:compose.groups')}</Typography>
        <Box role="group" aria-label={t('messages:compose.groups')} sx={GRID_SX}>
          <OptionCard
            checked={value.all}
            onToggle={toggleAll}
            disabled={disabled}
            avatar={<Avatar sx={GROUP_AVATAR_SX}><GroupsIcon /></Avatar>}
            title={t('messages:compose.everyone')}
            subtitle={t('messages:compose.member_count', { count: staff.length })}
          />
          {roles.map(role => {
            const selected = value.roleIds.includes(role.role_id);
            const members = role.staff_member_ids.filter(id => id !== currentUserId);
            return (
              <Tooltip
                key={role.role_id}
                placement="top"
                title={members.length ? members.map(id => nameById[id]).filter(Boolean).join(', ') : t('messages:compose.role_empty')}
              >
                <Box sx={{ minWidth: 0, display: 'grid' }}>
                  <OptionCard
                    checked={selected}
                    onToggle={() => toggleRole(role.role_id)}
                    disabled={disabled || (members.length === 0 && !selected)}
                    avatar={<Avatar sx={GROUP_AVATAR_SX}><RoleIcon /></Avatar>}
                    title={role.name}
                    subtitle={t('messages:compose.member_count', { count: members.length })}
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2.5 }}>{t('messages:compose.staff')}</Typography>
        {staff.length > 8 && (
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('messages:compose.staff_placeholder')}
            size="small"
            fullWidth
            disabled={disabled}
            sx={{ mb: 3 }}
            slotProps={{
              htmlInput: { 'aria-label': t('messages:compose.staff_placeholder') },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
        {visibleStaff.length > 0 ? (
          <Box
            role="group"
            aria-label={t('messages:compose.staff')}
            sx={{
              ...GRID_SX,
              // Scrolls on its own on a desktop; on a phone the full-screen dialog scrolls instead
              maxHeight: { sm: 300 },
              overflowY: { sm: 'auto' },
              pr: { sm: 2 },
            }}
          >
            {visibleStaff.map(member => (
              <OptionCard
                key={member.staff_member_id}
                checked={value.staffIds.includes(member.staff_member_id)}
                onToggle={() => toggleStaff(member.staff_member_id)}
                disabled={disabled}
                avatar={(
                  <ProfilePicture
                    profilePictureFileId={member.profile_picture_file_id}
                    name={member.name}
                    color={member.color}
                    size={26}
                    imageSize="32x32"
                    bgcolor="grey.400"
                  />
                )}
                title={member.name}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            {t('messages:compose.no_matches', { query: query.trim() })}
          </Typography>
        )}
      </Box>

      <Typography variant="body2" color={recipients.size ? 'text.secondary' : 'warning.main'}>
        {summary}
      </Typography>
    </Stack>
  );
}
